import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { cardClass, tertiaryText } from '../../../../styling/stylingHelper';
import type { FilterBlock } from '../../../../services/admin/cloudAgentTypes';
import type { ChatTurn } from '../../../../stores/appCloudAgentStore';
import { TurnActivity } from './TurnActivity';
import { AgentText } from './AgentText';
import { CloudAgentBlocks, GenerativePage } from './GenerativePage';
import { turnActivityState } from './turnActivityState';

const messageVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

interface ChatMessageProps {
  turn: ChatTurn;
  /** A `filter` block inside THIS turn changed — the page re-hydrates just this turn. */
  onFilterChange: (turnId: string, block: FilterBlock, selected: string[]) => void;
  /** True while this turn's blocks are being re-hydrated after a filter change. */
  isFiltering: boolean;
  /** Why this turn's last filter re-hydration failed (the previous blocks are kept). */
  filterError: string | null;
}

/**
 * One transcript entry. A user question renders as a right-aligned bubble; an assistant turn
 * renders as a one-line activity indicator, then streamed prose and its hydrated blocks.
 */
export const ChatMessage: React.FC<ChatMessageProps> = ({
  turn,
  onFilterChange,
  isFiltering,
  filterError,
}) => {
  const { t } = useTranslation();

  if (turn.role === 'user') {
    return (
      <motion.div variants={messageVariants} initial="hidden" animate="visible" className="flex justify-end">
        <div
          className={cn(
            'max-w-[85%] sm:max-w-[75%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap',
            'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100'
          )}
        >
          {turn.text}
        </div>
      </motion.div>
    );
  }

  const hasText = turn.text.trim().length > 0;
  const { showActivity, showCursor, showSummary } = turnActivityState(turn);

  return (
    <motion.div
      variants={messageVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col gap-4"
    >
      {turn.error && (
        <div className={cn(cardClass, 'p-4')}>
          {/* Translated frame; the raw server reason is appended only when there is one. */}
          <p className="text-sm text-red-500 dark:text-textRed-dark">
            {turn.error.reason
              ? t('cloudAgent.errorWithReason', { reason: turn.error.reason })
              : t('cloudAgent.error')}
          </p>
        </div>
      )}

      {/* One line while working, a muted "Checked N things · 12s" once done — both expandable. */}
      {(showActivity || showSummary) && <TurnActivity turn={turn} running={showActivity} />}

      {(hasText || showCursor) && (
        <div className="flex items-start">
          {hasText && <AgentText text={turn.text} />}
          {showCursor && (
            <motion.span
              className="inline-block w-[2px] h-4 ml-1 mt-1 bg-purple-500 dark:bg-purple-400 shrink-0"
              animate={{ opacity: [1, 0.15, 1] }}
              transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
        </div>
      )}

      {isFiltering && (
        <p className={cn(tertiaryText, 'text-[11px] flex items-center gap-1')}>
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('cloudAgent.filter.updating')}
        </p>
      )}

      {/* The filter failed: the blocks below are still the PREVIOUS selection, so say so. */}
      {filterError && !isFiltering && (
        <p className="text-[11px] flex items-center gap-1 text-red-500 dark:text-textRed-dark">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {t('cloudAgent.filter.failed', { reason: filterError })}
        </p>
      )}

      {/* A finished turn renders through the spec (hero card + blocks); while streaming, only the
          blocks hydrated so far exist, and a filter has no layout to rebuild from yet.
          `-mx-3` below sm cancels AdminLayout's own px-3 so cards go edge-to-edge on a phone —
          it exactly offsets the parent padding, so it cannot introduce horizontal overflow. */}
      <div className="max-sm:-mx-3">
        {turn.spec ? (
          <GenerativePage
            spec={turn.spec}
            onFilterChange={(block, selected) => onFilterChange(turn.id, block, selected)}
          />
        ) : (
          turn.blocks.length > 0 && <CloudAgentBlocks blocks={turn.blocks} />
        )}
      </div>
    </motion.div>
  );
};

export default ChatMessage;
